import { PATCH_TOGGLE_STAR } from "api"; // Import API endpoint
import { fetch } from "libs"; // Assuming you have a custom fetch wrapper
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const toggleStarStatus = async (imageId: string, isStarred: boolean) => {
  const url = PATCH_TOGGLE_STAR(imageId);

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ is_starred: !isStarred }), // Toggle the current status
  });

  if (!response.ok) {
    throw new Error("Failed to toggle star status");
  }

  return response.json(); // Return updated image data
};

const useToggleStar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ imageId, isStarred }: { imageId: string; isStarred: boolean }) =>
      toggleStarStatus(imageId, isStarred),

    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["image-generations"] });

      const previousData = queryClient.getQueryData(["image-generations"]);

      queryClient.setQueryData(["image-generations"], (oldData: any) => {
        if (!oldData) return oldData;

        const updatedPages = oldData.pages.map((page: any) => ({
          ...page,
          image_generations: page.image_generations.map((image: any) =>
            image.id === variables.imageId
              ? { ...image, is_starred: !variables.isStarred }
              : image
          ),
        }));

        return { ...oldData, pages: updatedPages };
      });

      return { previousData }; // ✅ Ensure an object is returned
    },

    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(["image-generations"], context.previousData);
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["image-generations"] }); // ✅ Refetch the latest data
    },
  });
};

export default useToggleStar;
